import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { ChevronDown } from '@lyearn/icons';
import { CheckMark } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { BaseMenuItem, BaseMenuList, Popover, Stack, Typography } from '@lyearn/ui';

import { labelBorderRadius } from './styles';
import { PreviewMenuProps } from './types';
import usePreviewLabelPopover from './usePreviewLabelPopover';

function PreviewMenu({ previewId, setPreviewId, versionConfigs }: PreviewMenuProps) {
  const { t } = useTranslation();
  const { togglePopover, showPopover, selectedLabel, menuItems, menuItemClick } =
    usePreviewLabelPopover({
      previewId,
      setPreviewId,
      versionConfigs,
    });

  return (
    <Popover open={showPopover} onOpenChange={togglePopover}>
      <Popover.Button
        className={cn(
          'cursor-pointer py-2 pe-8 ps-12',
          selectedLabel.labelClassNames,
          labelBorderRadius,
        )}>
        <Stack alignItems="center">
          <Typography className="whitespace-nowrap" variant="label-caps">
            {t(selectedLabel.label)}
          </Typography>
          <ChevronDown className={cn('h-24 w-24 ms-2', selectedLabel.icon)} />
        </Stack>
      </Popover.Button>
      <Popover.Content minWidth={25}>
        <BaseMenuList className={cn('rounded-8 bg-bg-primary p-8 shadow-01 outline-none')}>
          <Stack className="h-32">
            <Typography className="py-8 px-12" color="text-text-placeholder" variant="label-caps">
              {t('Common:Actions.Preview').toLocaleUpperCase()}
            </Typography>
          </Stack>
          {menuItems.map((items) => (
            <BaseMenuItem
              key={items.id}
              className="flex h-40 items-center rounded-8 px-12 py-8"
              data-id={items.id}
              onClick={() => menuItemClick(items.id!)}>
              <Typography className="text-text-primary" variant="body-short-02">
                {t(items.title)}
              </Typography>
              {previewId === items.id && (
                <CheckMark className="absolute h-20 w-20 end-10" color="text-icon-active" />
              )}
            </BaseMenuItem>
          ))}
        </BaseMenuList>
      </Popover.Content>
    </Popover>
  );
}

export default React.memo(PreviewMenu);
